import React, { useRef, useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  Chip,
  MenuItem, TextareaAutosize
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/ToastContext';
import { sendSuggestionAPI } from '../../api/suggestion';
import { useTheme } from '@mui/material/styles';
import { useConfig } from "../../services/ConfigContext";


const MAX_FILES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'application/pdf',
  'application/msword',
  'application/vnd.ms-excel',
  'application/zip',
  'application/x-zip-compressed',
];

const SuggestionCreate = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [feedbackType, setFeedbackType] = useState(1); // 1=Suggestion, 2=Issue
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const { config, loading } = useConfig();
  const isAttachment = config?.isAttachment;
  const [focused, setFocused] = useState(false);
  
  const MAX_TITLE_LENGTH = 500; 
  
  /* ---------- FILE HANDLING ---------- */
  const handleFiles = (fileList) => {
    const incoming = Array.from(fileList);
    const valid = [];

    if (files.length + incoming.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    for (const f of incoming) {
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`${f.name} exceeds 5MB`);
        continue;
      }
      if (!ALLOWED_TYPES.includes(f.type)) {
        toast.error(`${f.name} is not allowed`);
        continue;
      }
      valid.push(f);
    }

    setFiles((prev) => [...prev, ...valid]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const handleRemoveFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  /* ---------- SUBMIT ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !description.trim()) {
      toast.error('Title and Description are required');
      return;
    }

    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('FeedbackCategory', feedbackType);
      form.append('Title', title);
      form.append('Description', description);

      const userId = sessionStorage.getItem('userId');
      if (userId) form.append('CreatedBy', userId);

      files.forEach((f) => form.append('UploadedFiles', f));

      await sendSuggestionAPI(form);
      toast.success('Submitted successfully');

      navigate('/suggestions'); 
    } catch {
      toast.error('Submission failed');
    } finally {
      setSubmitting(false);
    }
  };
  /* ---------- UI ---------- */
  return (
    <Box sx={{p:2, pt:0.5, pl:0}}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" mb={1} sx={{ alignItems: 'center' }}>
        <Typography variant="body1" sx={{ color: 'text.primary' }}> 
          Create {feedbackType === 1 ? 'Suggestion' : 'Issue'}
        </Typography>

        <Button 
          variant="contained" 
          sx={{ 
            color: isDark ? '#121212' : 'white', 
            bgcolor: isDark ? '#90caf9' : '#00aaff',
            '&:hover': {
              bgcolor: isDark ? '#64b5f6' : '#0088cc',
            },
          }} 
          onClick={() => navigate('/suggestions')}
        >
          View List
        </Button>
      </Box>

      <Paper sx={{ display: "flex", flexDirection: "column", height: 'calc(100vh - 170px)', p:3, pt:4, overflow: 'auto', bgcolor: 'background.paper' }}>
        <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2}>
        {/* Type */}
        <TextField
          select
          size="small"
          label="Type"
          value={feedbackType}
          slotProps={{ input: { sx: { fontSize: '0.875rem' } } }}
          onChange={(e) => setFeedbackType(Number(e.target.value))}
        >
          <MenuItem  sx={{ fontSize: '0.875rem' }} value={1}>Suggestion</MenuItem>
          <MenuItem  sx={{ fontSize: '0.875rem' }} value={2}>Issue</MenuItem>
        </TextField>

        {/* Title */}
        <TextField
          size="small"
          label="Title"
          variant="outlined"
          value={title}
          onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE_LENGTH))}
          slotProps={{ 
            inputLabel: { sx: { fontSize: '0.875rem' } },
            htmlInput: { maxLength: MAX_TITLE_LENGTH }
          }}
          helperText={`${title.length}/${MAX_TITLE_LENGTH}`}
          FormHelperTextProps={{ sx: { textAlign: 'right', margin: 0, mt: 0.5 } }}
        />
        
        {/* Description */}
        <Box sx={{ position: 'relative' }}>
          <TextareaAutosize variant="body2"
            minRows={10}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            style={{
              width: '100%',
              padding: '18px 12px 8px',
              fontSize: '0.875rem',
              borderRadius: 4,
              border: focused 
                ? `2px solid ${theme.palette.primary.main}` 
                : `1px solid ${isDark ? theme.palette.divider : '#c4c4c4'}`,
              outline: 'none',
              fontFamily: 'inherit',
              resize: 'vertical',
              backgroundColor: 'transparent',
              color: theme.palette.text.primary,
            }}
          />
          <label
            style={{
              position: 'absolute',
              left: 12,
              top: focused || description ? -8 : 14,
              fontSize: focused || description ? '0.75rem' : '0.875rem',
              color: focused 
                ? theme.palette.primary.main 
                : (description ? theme.palette.text.secondary : theme.palette.text.primary),
              backgroundColor: theme.palette.background.paper,
              padding: '0 4px',
              transition: 'all 0.2s ease',
              pointerEvents: 'none',
            }}
          >
            Description *
          </label>
        </Box>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          onChange={(e) => handleFiles(e.target.files)}
        />

        {/* Drag & Drop */}
         {isAttachment && (
           <Box>
        <Paper
          variant="outlined"
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current.click()}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#fafafa',
            borderColor: 'divider',
            "&:hover": {
              bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f0f0f0'
            }
          }}
        >
          <CloudUploadIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Box display="flex" flexDirection="column" ml={2}>
            <Typography fontSize={14} color="text.secondary" mb={1}>
              Drag & drop files here or click to browse.
            </Typography>
            <Typography fontSize={12} color="text.secondary">
              Supported formats: PNG, JPG, JPEG, PDF, DOC, XLS, ZIP (Max size: 5 MB)
            </Typography>
          </Box>
        </Paper>
        <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
          {files.map((f, i) => (
            <Chip
              key={i}
              label={f.name}
              onDelete={() => handleRemoveFile(i)}
              deleteIcon={<CloseIcon />}
            />
          ))}
        </Box>
        </Box>)}

        {/* Submit */}
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </Button>
        </Box>
      </Box>
      </Paper>
    </Box>
  );
};

export default SuggestionCreate;